import React from 'react';
import { Mail, Phone, MapPin, Clock, Building, Users, GraduationCap, Home, ExternalLink, Music } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

export const Contact = () => {
  const { t } = useTranslation();
  
  const contactSections = [
    {
      title: t('contact.sections.board', 'Styret'),
      icon: Users,
      items: [
        { type: 'email', value: 'styret@veitvet-sportsklubb.no' }
      ]
    },
    {
      title: t('contact.sections.admin', 'Administrasjon'),
      icon: Building,
      items: [
        { type: 'person', name: t('contact.roles.manager', 'Daglig leder'), value: 'Nickolas Paynter' },
        { type: 'email', value: 'nickolas.paynter@veitvet-sportsklubb.no' },
        { type: 'phone', value: '+47 907 39 749', hours: t('contact.hoursWeekday', 'Mandag - Fredag, 10:00 - 15:00') }
      ]
    },
    {
      title: t('contact.sections.finance', 'Økonomi'),
      icon: Building,
      items: [
        { type: 'person', name: t('contact.roles.cfo', 'Økonomisjef'), value: 'Nina Aggarwal (Oslo idrettslag regnskapskontor)' },
        { type: 'email', value: 'nina_aggarwal@idrettsforbundet.no' },
        { type: 'phone', value: '+47 406 17 620' }
      ]
    },
    {
      title: t('contact.sections.academy', 'Veitvet SK Fotballakademi'),
      icon: GraduationCap,
      items: [
        { type: 'email', value: 'fotballakademi@veitvet-sportsklubb.no' }
      ]
    },
    {
      title: t('contact.sections.dance', 'Veitvet SK Dansegruppa'),
      icon: Music,
      items: [
        { type: 'note', value: t('contact.forQuestions', 'For spørsmål og påmelding') },
        { type: 'phone', value: '+47 90 72 51 52' }
      ]
    },
    {
      title: t('contact.sections.rentals', 'Utleie'),
      icon: Home,
      items: [
        { type: 'email', value: 'utleie@veitvet-sportsklubb.no', note: t('contact.replyNote', 'Vi svarer på alle henvendelser innen 24 timer. Hvis du ikke har mottatt svar, vennligst sjekk spam-mappen din.') },
        { type: 'phone', value: '+47 907 15 725', hours: t('contact.hoursEvening', 'Mandag - Søndag, 16:00 - 22:00') }
      ]
    }
  ];

  return (
    <div data-testid="contact-page">
      <SEO 
        title={t('pg.Contact.92316d')} 
        description={t('pg.Contact.a25907')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80"
            alt={t('pg.Contact.c3483e')}
            loading="eager"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('contact.subtitle', 'Kontakt oss')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('contact.title', 'TA KONTAKT')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {t('contact.description', 'Vi er her for å hjelpe deg. Finn riktig kontaktperson nedenfor.')}
          </p>
        </div>
      </section>

      {/* Main Contact Info */}
      <section className="py-12 bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-green-500" aria-hidden="true" />
              </div>
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{t('pg.Contact.3d4125')}</p>
                <a href="mailto:nickolas.paynter@veitvet-sportsklubb.no" className="text-slate-900 dark:text-white font-medium hover:text-green-500 transition-colors" aria-label={t('pg.Contact.454a57')}>
                  nickolas.paynter@veitvet-sportsklubb.no
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-green-500" aria-hidden="true" />
              </div>
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{t('pg.Contact.0975cf')}</p>
                <a href="tel:+4790739749" className="text-slate-900 dark:text-white font-medium hover:text-green-500 transition-colors" aria-label={t('pg.Contact.407688')}>
                  +47 907 39 749
                </a>
                <p className="text-slate-400 dark:text-slate-500 text-xs">{t('pg.Contact.a77c92')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-green-500" aria-hidden="true" />
              </div>
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{t('pg.Contact.f268e3')}</p>
                <p className="text-slate-900 dark:text-white font-medium">{t('pg.Contact.02ca19')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Sections */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Contact.8ed541')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Contact.70d48a')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contactSections.map((section, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:border-green-500/30 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">{section.title}</h3>
                </div>
                <div className="space-y-3">
                  {section.items.map((item, i) => (
                    <div key={i}>
                      {item.type === 'person' && (
                        <div className="mb-1">
                          <span className="text-slate-400 text-sm">{item.name}:</span>
                          <p className="text-slate-900 dark:text-white">{item.value}</p>
                        </div>
                      )}
                      {item.type === 'note' && (
                        <p className="text-slate-500 text-sm">{item.value}</p>
                      )}
                      {item.type === 'email' && (
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                          <div>
                            <a 
                              href={`mailto:${item.value}`} 
                              className="text-slate-600 hover:text-green-500 transition-colors text-sm break-all"
                            >
                              {item.value}
                            </a>
                            {item.note && (
                              <p className="text-slate-400 text-xs mt-1">{item.note}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {item.type === 'phone' && (
                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                          <div>
                            <a 
                              href={`tel:${item.value.replace(/\s/g, '')}`} 
                              className="text-slate-600 hover:text-green-500 transition-colors text-sm"
                            >
                              {item.value}
                            </a>
                            {item.hours && (
                              <p className="text-slate-400 text-xs flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {item.hours}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organization Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Contact.eab954')}</p>
              <h2 className="text-4xl font-bold tracking-tighter text-slate-900 mb-6">
                {t('pg.Contact.8dee78')}
              </h2>
              <div className="space-y-4 text-slate-500 dark:text-slate-400">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-slate-900 font-medium">{t('pg.Contact.f268e3')}</p>
                    <p>{t('pg.Contact.02ca19')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-slate-900 font-medium">{t('pg.Contact.baa401')}</p>
                    <p>971 528 117</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-100 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('pg.Contact.cb38f3')}</h3>
              <p className="text-slate-500 mb-6">
                {t('pg.Contact.e9ec2a')}
              </p>
              <a 
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-member-btn"
              >
                <Button className="w-full bg-green-500 text-black font-bold uppercase tracking-wider py-6 hover:bg-green-500/90">
                  {t('pg.Contact.b52509')}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
